import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { StateLanguage } from '../languages/config/StateLanguage';
import { AppServiceToken } from '../models/app.service.model';
import { CompanyToken } from '../models/company.model';
import { GlobalSwal } from '../styles/swal.style';
import { TreasuryAccount } from '../models/treasury.account.model';
import { offcanvasAsideShow } from '../utils/offcanvas.util';
import { modalShow } from '../utils/modal.util';

import AppModalNotificationInvitation from './modal/ModalInvitationCreate';
import logo from '../assets/themes/svg/logo-official-dark.svg';

export interface AppHeaderProps {
  setActiveDrawer: Dispatch<SetStateAction<{value: string, aside: string, button: string}>>,
  activeDrawer: {value: string, aside: string, button: string},
  route: {path: {root: string, branch: string} | null, company: boolean} | null,
  appServiceForUser: AppServiceToken | null,
  companyForUser: CompanyToken | null,
  treasuryAccountsForCompany: TreasuryAccount[] | null
};

const AppHeader: React.FunctionComponent<AppHeaderProps> = ({activeDrawer, setActiveDrawer, route, appServiceForUser, companyForUser, treasuryAccountsForCompany}) => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const executeNavigationOption = (item: string) => {
    if (treasuryAccountsForCompany && treasuryAccountsForCompany.length > 0) {
      switch (item) {
        case 'quote':
          navigate('/main/sale/quote/create')
          break;
        case 'order':
          navigate('/main/sale/order/create')
          break;
        case 'invoice':
          navigate('/main/sale/invoice/create')
          break;
        case 'provider order':
          navigate('/main/purchase/order/create')
          break;
        case 'provider invoice':
          navigate('/main/purchase/invoice/create')
          break;
        case 'expense':
          navigate('/main/purchase/expense/create')
          break;
      }
    } else {
      GlobalSwal.fire({
        title: lang.labels.thereAreNoTreasuryAccounts,
        text: `${lang.labels.toCreateA} ${item}, ${lang.labels.theCompanyContainTreasutyAccount}.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.okGotIt,
        cancelButtonText: lang.labels.noCancel
      }).then( (result) => {
        if (result.isConfirmed) {
          navigate('/main/treasury/create')
        }
      })
    }
  }

  const executeShowModalInvitationCreate = () => {
    modalShow('modal-invitation-create')
  }

  const executeNavbarAside = () => {
    setActiveDrawer({...activeDrawer, value: 'on', aside: 'on'})
    offcanvasAsideShow('navbar-aside', 'overlay-drawer')
  }

  return (
    <>
      { route &&
        <div id="wrapper-header" className="header">
          <div className="container-xxl d-flex align-items-center justify-content-between">
            <div className="page-title d-flex flex-column align-items-start justify-content-center flex-wrap">
              <h2 className="text-dark fw-bold mb-1">{companyForUser ? companyForUser.name : lang.labels.digitalAdmin}</h2>
              <ul className="breadcrumb breadcrumb-line text-muted fw-bold fs-base">
                { route.path
                  ?
                  <>
                    <li className="breadcrumb-item text-muted">{lang.labels.digitalAdmin} - {route.path.root}</li>
                    <li className="breadcrumb-item text-dark">{route.path.branch}</li>
                  </>
                  :
                  <>
                    <li className="breadcrumb-item text-muted">{lang.labels.digitalAdmin}</li>
                    <li className="breadcrumb-item text-dark">{route ? lang.labels.dashboard : lang.labels.welcome}</li>
                  </>
                }
              </ul>
            </div>
            <div className="d-flex d-lg-none align-items-center ms-n2 me-2">
              <div className="btn btn-icon btn-active-icon-primary" onClick={executeNavbarAside}>
                <span className="svg-icon svg-icon-2x">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="black" />
                    <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="black" />
                  </svg>
                </span>
              </div>
              <div className="d-flex align-items-center cursor-pointer ms-1">
                <img src={logo} alt={lang.labels.digitalAdmin} className="h-20px" />
              </div>
            </div>
            { !route.path && companyForUser &&
              <div className="d-flex flex-shrink-0">
                <div className="d-flex ms-3">
                  <button className="btn btn-light-primary" type="button" onClick={executeShowModalInvitationCreate}>{lang.labels.sendInvitation}</button>
                </div>
                <div className="d-flex ms-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="default btn-icon btn-primary">
                      <span className="svg-icon svg-icon-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-info w-275px py-3 fs-6 fw-bold" align="end">
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('quote')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addQuote}</div>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('order')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addOrder}</div>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('invoice')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addInvoice}</div>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('provider order')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addProviderOrder}</div>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('provider invoice')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addProviderInvoice}</div>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item px-3" onClick={() => executeNavigationOption('expense')}>
                        <div className="menu-link fw-bold m-0">{lang.labels.addExpense}</div>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            }
          </div>
        </div>
      }
      <AppModalNotificationInvitation appServiceForUser={appServiceForUser} companyForUser={companyForUser}></AppModalNotificationInvitation>
    </>
  )
}

export default AppHeader;
